import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Sparkles, Headphones, Check } from "lucide-react";
import { useCruise } from "../context/CruiseContext";
import { getSessionId } from "../lib/session";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_BY_CRUISE = {
  "escape-2026": ["When are eDocs ready?", "How do I check in?", "How much are cabins?", "What's the host hotel?"],
  "europe-2027": ["Tell me about SuncéBeat", "What's the itinerary?", "How much are cabins?", "When is final payment?"],
  "caribbean-2027": ["When does it sail?", "Which islands do we visit?", "How much are cabins?", "What's included?"],
};

const linkify = (text) => {
  const parts = String(text).split(/(https?:\/\/[^\s)]+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="underline break-all" style={{ color: "var(--hh-gold)" }}>
        {p}
      </a>
    ) : (
      <React.Fragment key={i}>{p}</React.Fragment>
    )
  );
};

const AssistantChat = () => {
  const { EVENT } = useCruise();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "" });
  const endRef = useRef(null);
  const interacted = useRef(false);

  const quick = QUICK_BY_CRUISE[EVENT.id] || QUICK_BY_CRUISE["escape-2026"];

  const greeting = {
    role: "assistant",
    text: `Hey House Head! 🎧 Ask me anything about ${EVENT.tagline} — dates, the ship, ports, pricing, hotels and more.`,
  };

  useEffect(() => {
    setMsgs([greeting]);
    setShowLead(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [EVENT.id]);

  // Proactive greeting: auto-open once per browser session after a few seconds (desktop only).
  useEffect(() => {
    let t;
    const isMobile = typeof window !== "undefined" &&
      (window.matchMedia("(max-width: 1023px)").matches || window.matchMedia("(pointer: coarse)").matches);
    try {
      if (!isMobile && !sessionStorage.getItem("hhas_ai_opened")) {
        t = setTimeout(() => {
          if (!interacted.current) {
            setOpen(true);
            sessionStorage.setItem("hhas_ai_opened", "1");
          }
        }, 6000);
      }
      if (sessionStorage.getItem("hhas_lead_done")) setLeadDone(true);
    } catch (e) {}
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (open) endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open, loading, showLead]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    interacted.current = true;
    try { sessionStorage.setItem("hhas_ai_opened", "1"); } catch (e) {}
    setInput("");
    setMsgs((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/assistant/ask`, {
        session_id: getSessionId(),
        cruise: EVENT.id,
        message: q,
      });
      setMsgs((m) => [...m, { role: "assistant", text: res.data.answer }]);
      if (res.data.lead && !leadDone) setShowLead(true);
    } catch (e) {
      setMsgs((m) => [...m, { role: "assistant", text: "I'm having trouble right now. Tap \u201cTalk to a human\u201d or call 470-632-8963." }]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.name.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email)) return;
    try {
      await axios.post(`${API}/assistant/lead`, {
        session_id: getSessionId(),
        cruise: EVENT.id,
        name: lead.name.trim(),
        email: lead.email.trim(),
        phone: lead.phone.trim(),
        note: `Interested in ${EVENT.tagline}`,
      });
      setLeadDone(true);
      setShowLead(false);
      try { sessionStorage.setItem("hhas_lead_done", "1"); } catch (er) {}
      setMsgs((m) => [...m, { role: "assistant", text: `Got it, ${lead.name.trim().split(" ")[0]}! 🙌 Our team will reach out at ${lead.email.trim()} to help you lock in your cabin. You can also call 470-632-8963 anytime.` }]);
    } catch (er) {
      setMsgs((m) => [...m, { role: "assistant", text: "Couldn't save that just now — please call 470-632-8963 and we'll help you book." }]);
      setShowLead(false);
    }
  };

  const talkToHuman = () => {
    try {
      const isMobile = typeof window !== "undefined" &&
        (window.matchMedia("(max-width: 1023px)").matches || window.matchMedia("(pointer: coarse)").matches);
      if (isMobile) {
        // On mobile, place a phone call straight to the team.
        window.location.href = "tel:+14706328963";
        return;
      }
      if (window.Tawk_API) {
        if (typeof window.Tawk_API.showWidget === "function") window.Tawk_API.showWidget();
        if (typeof window.Tawk_API.maximize === "function") window.Tawk_API.maximize();
        setOpen(false);
      } else {
        window.location.href = "tel:+14706328963";
      }
    } catch (e) {}
  };

  return (
    <>
      <button
        onClick={() => { interacted.current = true; setOpen((o) => !o); }}
        data-testid="assistant-launcher"
        aria-label="Open cruise assistant"
        className="fixed right-5 lg:right-auto lg:left-5 bottom-20 lg:bottom-6 z-[60] flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-[#111111] border border-white/15 text-white shadow-[0_8px_30px_rgba(0,0,0,0.5)] hover:border-white/40 transition-colors"
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#E50914]">
          {open ? <X size={16} /> : <Sparkles size={16} />}
        </span>
        <span className="font-cond uppercase tracking-widest text-xs">Ask AI</span>
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed left-4 right-4 sm:left-5 sm:right-auto bottom-36 lg:bottom-24 z-[60] sm:w-[370px] h-[62vh] sm:h-[520px] flex flex-col rounded-2xl overflow-hidden bg-[#0C0C0C] border border-white/12 shadow-[0_20px_60px_rgba(0,0,0,0.7)]"
        >
          <div className="shrink-0 px-4 py-3 bg-[#111111] border-b border-white/10 flex items-center gap-3">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#E50914] text-white">
              <Sparkles size={17} />
            </span>
            <div className="min-w-0">
              <p className="font-cond font-bold uppercase tracking-wide text-white text-sm leading-none">Cruise Assistant</p>
              <p className="text-white/45 text-xs truncate mt-0.5" style={{ color: "var(--hh-gold)" }}>{EVENT.tagline}</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" aria-label="Close" className="ml-auto text-white/50 hover:text-white">
              <X size={18} />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`assistant-msg-${m.role}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-[#E50914] text-white rounded-br-sm"
                      : "bg-[#1A1A1A] text-white/90 rounded-bl-sm border border-white/10"
                  }`}
                >
                  {m.role === "assistant" ? linkify(m.text) : m.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#1A1A1A] border border-white/10 rounded-2xl rounded-bl-sm px-3.5 py-3">
                  <span className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}

            {/* Booking-intent lead capture */}
            {showLead && !leadDone && (
              <form onSubmit={submitLead} data-testid="assistant-lead-form" className="rounded-2xl border border-[#E50914]/40 bg-[#170a0b] p-3.5 space-y-2.5">
                <p className="text-white text-sm font-semibold">Want us to help you book? 🚢</p>
                <p className="text-white/55 text-xs">Drop your name & email and our team will follow up to lock in your cabin.</p>
                <input
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  placeholder="Full name"
                  data-testid="assistant-lead-name"
                  className="w-full bg-[#1A1A1A] border border-white/15 rounded-lg px-3 h-10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E50914]"
                />
                <input
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="Email address"
                  data-testid="assistant-lead-email"
                  className="w-full bg-[#1A1A1A] border border-white/15 rounded-lg px-3 h-10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E50914]"
                />
                <input
                  type="tel"
                  value={lead.phone}
                  onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  placeholder="Phone (optional)"
                  data-testid="assistant-lead-phone"
                  className="w-full bg-[#1A1A1A] border border-white/15 rounded-lg px-3 h-10 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E50914]"
                />
                <div className="flex items-center gap-2">
                  <button type="submit" data-testid="assistant-lead-submit" className="flex-1 bg-[#E50914] hover:bg-[#c20710] text-white font-cond uppercase tracking-widest text-xs rounded-full h-10">
                    Send My Info
                  </button>
                  <button type="button" onClick={() => setShowLead(false)} className="text-white/40 hover:text-white text-xs px-2">
                    Skip
                  </button>
                </div>
              </form>
            )}

            {leadDone && (
              <div className="flex items-center gap-2 text-xs text-[#1B8A5A] justify-center">
                <Check size={14} /> We've got your info — talk soon!
              </div>
            )}

            {msgs.length <= 1 && !loading && !showLead && (
              <div className="flex flex-wrap gap-2 pt-1">
                {quick.map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    data-testid="assistant-quick-chip"
                    className="text-xs font-cond uppercase tracking-wide rounded-full border border-white/15 text-white/75 px-3 py-1.5 hover:border-white/40 hover:text-white transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>

          <button
            onClick={talkToHuman}
            data-testid="assistant-talk-human"
            className="shrink-0 flex items-center justify-center gap-2 text-xs font-cond uppercase tracking-widest text-white/70 hover:text-white py-2 border-t border-white/10 bg-[#111111]"
          >
            <Headphones size={14} /> Talk to a human
          </button>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="shrink-0 flex items-center gap-2 p-3 border-t border-white/10 bg-[#0C0C0C]"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              data-testid="assistant-input"
              placeholder="Ask about this cruise…"
              className="flex-1 bg-[#1A1A1A] border border-white/15 rounded-full px-4 h-11 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#E50914]"
            />
            <button
              type="submit"
              disabled={loading || !input.trim()}
              data-testid="assistant-send"
              aria-label="Send"
              className="flex items-center justify-center w-11 h-11 rounded-full bg-[#E50914] text-white disabled:opacity-40"
            >
              <Send size={17} />
            </button>
          </form>
        </div>
      )}
    </>
  );
};

export default AssistantChat;
